import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './childTable.module.scss'
import { TableCell, TableRow } from '@mui/material'
import { Check, ChildCareOutlined, Close, Delete, ErrorOutline } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { Role } from '@/types/Role'
import Desktop from '@/modules/desktopMobile/desktop'
import ConfirmDeleteChildDialog from './Dialog/confirmDeleteChildDialog'
import moment from 'moment-timezone'
import { Child, ChildDaycare } from '@/types/Child'
import { useAuth } from '@/modules/context/AuthContext'
import { ParentDaycare, ParentWithChild } from '@/types/Parent'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { UPDATE_PARENT_LINK } from '@/modules/API/parent/parentHandler'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { SelectOption } from '@/types/Select'

interface ChildBodyCellProps {
    sections: SelectOption<number>[]
    groups: SelectOption<number>[]
    daycare?: ParentDaycare
    child: Child
    isNanny: boolean
    isRpeOrLaep: boolean
    deleteChild: (childId: number) => void
    handleRefetchParent: () => Promise<void>
    parentId: number
    haveDaycare: boolean
}

const ChildBodyCell: FunctionComponent<ChildBodyCellProps> = ({
    sections,
    groups,
    daycare,
    child,
    isNanny,
    isRpeOrLaep,
    deleteChild,
    handleRefetchParent,
    parentId,
    haveDaycare
}: ChildBodyCellProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const daycareLink: ChildDaycare | undefined = daycare
        ? child.daycares.find((daycareData: ChildDaycare): boolean => daycareData.daycareId === daycare.daycareId)
        : undefined
    const parentLink: ParentWithChild | undefined = daycare
        ? daycare.childs.find((childData: ParentWithChild): boolean => childData.childId === child.id)
        : undefined

    const [isRemoveChildDialog, setRemoveChildDialog] = useState<boolean>(false)

    const [complement, setComplement] = useState<string>(daycareLink?.complement || '')
    const [section, setSection] = useState<number | undefined>(
        sections.find((section) => {
            const secId: number | undefined = daycareLink?.sections.find(
                (sectionData) => sectionData.isPrimary
            )?.sectionId
            return secId ? secId === section.value : false
        })?.value
    )

    const [requestSlots, setRequestSlots] = useState<boolean>(
        child.daycares.find((daycareData) => {
            return daycareData.daycareId === daycare?.daycareId
        })?.request || false
    )

    const [visible, setVisible] = useState<boolean>(daycareLink?.visible || false)

    useEffect((): void => {
        setSection(
            sections.find((section) => {
                const secId: number | undefined = daycareLink?.sections.find(
                    (sectionData) => sectionData.isPrimary
                )?.sectionId
                return secId ? secId === Number(section.value) : false
            })?.value
        )
        setComplement(daycareLink?.complement || '')
        setVisible(daycareLink?.visible || false)

        setRequestSlots(parentLink && parentLink.request ? parentLink.request : false)
    }, [sections, parentLink, daycareLink, groups, child, daycare])

    function handleRequestSlot(): void {
        setRequestSlots(!requestSlots)
        updateParentLink()
    }

    const [updateParentLink] = useMutation(UPDATE_PARENT_LINK, {
        variables: {
            child_id: child.id,
            parent_id: parentId,
            daycare_id: daycare?.daycareId,
            request: !requestSlots
        },
        onError: (): void => {
            showNotificationSuccess(t('errorParentLinkUpdate'))
        },
        onCompleted: (): void => {
            showNotificationError(t('parentLinkUpdated'))
            handleRefetchParent()
        }
    })

    return (
        <TableRow>
            {((daycare && haveDaycare) || user?.role === Role.cald) && (
                <Desktop>
                    <TableCell>
                        <div className={styles.TableCell}>
                            <ChildCareOutlined
                                onClick={() => navigate('/childs?child=' + child.id)}
                                className={styles.Icon}
                            />
                        </div>
                    </TableCell>
                </Desktop>
            )}
            <TableCell>{`${child.firstname} ${complement ? ' - ' + complement : ''}`}</TableCell>
            <Desktop>
                <TableCell>{moment(child.birthdate, 'DD-MM-YYYY').format('DD/MM/YYYY')}</TableCell>
            </Desktop>
            {daycare && haveDaycare && (
                <Desktop>
                    {user?.currentDaycareRights.canReadChild && !(isNanny && isRpeOrLaep) && (
                        <TableCell>
                            {section ? (
                                <span className={styles.Text}>{sections.find((s) => s.value === section)?.name}</span>
                            ) : (
                                <ErrorOutline
                                    className={styles.IconWithoutSection}
                                    onClick={() => navigate('/childs?child=' + child.id)}
                                />
                            )}
                        </TableCell>
                    )}
                    {user?.currentDaycareRights.canReadOccasionalSlot && (
                        <TableCell>
                            <div className={styles.TableCell}>
                                <Checkbox
                                    checked={requestSlots}
                                    disabled={!user?.currentDaycareRights.canWriteOccasionalSlot}
                                    onChange={handleRequestSlot}
                                    className={styles.Checkbox}
                                />
                            </div>
                        </TableCell>
                    )}
                    {daycare && (
                        <TableCell>
                            <div className={styles.TableCell}>
                                {visible ? (
                                    <Check
                                        className={styles.CheckIcon}
                                        onClick={() => navigate('/childs?child=' + child.id)}
                                    />
                                ) : (
                                    <Close
                                        className={styles.CloseIcon}
                                        onClick={() => navigate('/childs?child=' + child.id)}
                                    />
                                )}
                            </div>
                        </TableCell>
                    )}
                </Desktop>
            )}
            {(haveDaycare || user?.role === Role.cald) && (
                <TableCell>
                    <div className={styles.TableCell}>
                        <a
                            href={'/childs?child=' + child.id}
                            onClick={(e: React.MouseEvent): void => {
                                if (!(e.ctrlKey || e.metaKey)) {
                                    e.preventDefault()
                                    navigate('/childs?child=' + child.id)
                                }
                            }}
                        >
                            <VisibilityIcon className={styles.Icon} />
                        </a>
                        {user?.role === Role.cald && (
                            <>
                                <Delete onClick={() => setRemoveChildDialog(true)} className={styles.Icon} />
                                <ConfirmDeleteChildDialog
                                    deleteChild={deleteChild}
                                    childId={child.id}
                                    open={isRemoveChildDialog}
                                    childFirstname={child.firstname}
                                    handleClose={() => setRemoveChildDialog(false)}
                                />
                            </>
                        )}
                    </div>
                </TableCell>
            )}
        </TableRow>
    )
}

export default ChildBodyCell
